// src/utils/thermalReceiptPrint.js
// Shared 80mm thermal receipt printing — used by the offline sales terminal receipt and the
// offline sales order "Print Receipt (80mm)" reprint, so both slips print identically.
//
// The receipt markup is copied into an isolated iframe with its own CSS, so the app layout
// can't add blank pages, clip long receipts or fall back to A4.

// Receipt markup uses these CSS-module class names (receiptMetaRow, receiptItem, …); pass the
// module's `styles` object so the print rules target the hashed names.
// 80mm roll → ~72mm printable width. Solid black only: thermal printers drop greys.
export const buildThermalReceiptCss = (styles) => `
  @page { margin: 0; } /* exact 80mm × height size is added after measuring, in printThermalReceipt */
  * { box-sizing: border-box; }
  html, body { margin: 0; padding: 0; background: #fff; }
  body {
    width: 80mm;
    font-family: Arial, Helvetica, sans-serif;
    font-size: 12px;
    line-height: 1.35;
    color: #000;
    -webkit-print-color-adjust: exact;
    print-color-adjust: exact;
  }
  .receipt80 { width: 72mm; margin: 0 auto; padding: 0 0 6mm; }
  .receipt80 * { color: #000 !important; }
  .receipt80 h2, .receipt80 p { margin: 0; }
  .${styles.receiptHeader}, .${styles.receiptFooter} {
    text-align: center; font-size: 11px; white-space: pre-wrap; margin: 2mm 0;
  }
  .${styles.receiptLogoWrap} { display: flex; justify-content: center; margin: 0 0 2mm; }
  .${styles.receiptLogo} { display: block; margin: 0 auto; max-width: 45mm; max-height: 22mm; object-fit: contain; filter: grayscale(1) contrast(1.2); }
  .${styles.receiptShop} { text-align: center; font-size: 13px; font-weight: 700; margin-bottom: 2mm; }
  .${styles.receiptMetaRow}, .${styles.receiptTotalRow}, .${styles.receiptItem} {
    display: flex; justify-content: space-between; align-items: flex-start; gap: 2mm; padding: 0.6mm 0;
  }
  .${styles.receiptMetaRow} span:last-child,
  .${styles.receiptTotalRow} > :last-child { text-align: right; white-space: nowrap; }
  .${styles.receiptItemName} { flex: 1; min-width: 0; word-break: break-word; }
  .${styles.receiptItemQty} { white-space: nowrap; }
  .${styles.receiptItemTotal} { white-space: nowrap; text-align: right; min-width: 18mm; font-weight: 600; }
  .${styles.receiptDivider} { border: 0; border-top: 1px dashed #000; margin: 2mm 0; }
  .${styles.receiptGrandTotal} {
    font-size: 14px; border-top: 1px solid #000; margin-top: 1mm; padding-top: 1mm;
  }
  .${styles.receiptPaymentHeader} { font-weight: 700; margin-bottom: 1mm; }
  .${styles.receiptLoyalty} { text-align: center; font-weight: 600; margin: 2mm 0; }
`;

// Receipt shows at most 6 letters of the cashier name: "Dilini Nadeeshani" → "Dilini..."
export const shortCashierName = (name = '') =>
  (name && name.length > 6 ? `${name.slice(0, 6)}...` : name || '');

export const printThermalReceipt = (receiptElement, { title = 'Receipt', css }) => {
  if (!receiptElement) return;

  const iframe = document.createElement('iframe');
  iframe.setAttribute('aria-hidden', 'true');
  iframe.style.cssText = 'position:fixed;right:0;bottom:0;width:0;height:0;border:0;visibility:hidden;';
  document.body.appendChild(iframe);

  const doc = iframe.contentDocument;
  doc.open();
  doc.write(`<!doctype html><html><head><meta charset="utf-8"><title>${title}</title>
    <style>${css}</style></head>
    <body><div class="receipt80">${receiptElement.innerHTML}</div></body></html>`);
  doc.close();

  const cleanup = () => setTimeout(() => iframe.remove(), 500);
  iframe.contentWindow.onafterprint = cleanup;

  // Wait for the logo (and any other images) to load — max 3s — so it isn't printed blank
  const imagesReady = Promise.race([
    Promise.all(Array.from(doc.images).map(img => (
      img.complete ? Promise.resolve() : new Promise(res => { img.onload = res; img.onerror = res; })
    ))),
    new Promise(res => setTimeout(res, 3000)),
  ]);

  imagesReady.then(() => setTimeout(() => {
    // `size: 80mm auto` is invalid CSS (Chrome ignores it and falls back to the driver's paper),
    // so measure the receipt and set an exact 80mm × <height>mm page — one continuous slip
    const heightPx = doc.querySelector('.receipt80')?.scrollHeight || doc.body.scrollHeight;
    const heightMm = Math.ceil(heightPx * 25.4 / 96) + 4;
    const pageStyle = doc.createElement('style');
    pageStyle.textContent = `@page { size: 80mm ${heightMm}mm; margin: 0; }`;
    doc.head.appendChild(pageStyle);

    iframe.contentWindow.focus();
    iframe.contentWindow.print();
    setTimeout(cleanup, 60000); // fallback if afterprint never fires
  }, 100));
};
